import { V1CloudEventsInsightCategory } from '@ariksa/audittrail/api';
import { CloudProviders } from '@ariksa/cloud-account/api';
import { ComplianceStandards } from '@ariksa/compliance-policies/api';
import { SupportedServices } from '@ariksa/data-scanning/api';
import {
  Categories,
  NativeResources,
  Resources,
} from '@ariksa/inventory-core/api';
import { ReportTypes } from '@ariksa/reporting';
import { CloudProvider } from '@ariksa/reporting/api';
import { includes } from 'lodash';
import { ApplicationEnum, LibraryType } from 'services/Vulnerability/types';
import { customTheme } from 'theme';

import { CustomIconTypes, IconTypes } from 'components/Icons';

export const getIconColor = (type: string, useCustomColor?: boolean) => {
  if (useCustomColor) return type;
  let color = type;

  switch (type) {
    case Resources.Analytics:
    case NativeResources.ElasticMapReduce:
      color = '#F01B29'; //rgb: 240,27,41
      break;
    case NativeResources.ApiGatewayRestApi:
    case NativeResources.ApiGatewayDomain:
    case Resources.ApiGateway:
    case NativeResources.GcpapiGateway:
      color = '#09C2FF'; //rgb: 9,194,255
      break;
    case NativeResources.EcsService:
    case NativeResources.ElasticContainerService:
    case NativeResources.AcmCertificate:
    case NativeResources.IamUserAccessKey:
    case NativeResources.KeyManagementService:
    case NativeResources.RdsCertificate:
    case NativeResources.Secret:
    case NativeResources.AzureSecrets:
    case NativeResources.SshKeyPair:
    case NativeResources.GcpSecretManager:
    case Resources.Key:
    case Resources.Secrets:
    case Resources.Certificate:
    case Resources.ContainerCluster:
    case NativeResources.GcpCloudKms:
    case NativeResources.GcpsqlsslCerts:
    case 'EcsTask':
      color = '#E02947'; //rgb: 224,41,71
      break;
    case NativeResources.ElastiCache:
    case NativeResources.ElastiCacheReplicationGroup:
    case NativeResources.ElasticSearch:
    case NativeResources.GcpMemorystoreRedis:
      color = '#056147'; //rgb: 5,97,71
      break;
    case NativeResources.EcsCluster:
    case NativeResources.ElasticKubernetesService:
    case NativeResources.RdsCluster:
    case NativeResources.Container:
    case CustomIconTypes.IdentityAuthorization:
    case NativeResources.Gcpgke:
    case NativeResources.GcpContainer:
      color = '#623CE4'; //rgb:98, 60, 228
      break;
    case NativeResources.RedshiftCluster:
    case NativeResources.BackupPlan:
    case NativeResources.DocumentDb:
    case NativeResources.DynamoDb:
    case NativeResources.DocumentDbCluster:
    case NativeResources.EbsSnapshot:
    case NativeResources.ElasticFileSystem:
    case NativeResources.Neptune:
    case NativeResources.NeptuneCluster:
    case NativeResources.ElasticBlockStorage:
    case NativeResources.GcpBucket:
    case Resources.ObjectStorage:
    case Resources.BlockStorage:
    case NativeResources.RelationalDatabaseService:
    case NativeResources.RdsSnapshot:
    case NativeResources.RdsEventSubscription:
    case CustomIconTypes.DataSecurity:
    case NativeResources.GcpBigQuery:
    case NativeResources.GcpFilestore:
    case NativeResources.GcpFirestore:
    case NativeResources.GcpBigtableInstances:
    case NativeResources.GcpCloudSql:
    case NativeResources.GcpPersistentDisk:
    case NativeResources.GcpFilestoreBackup:
    case NativeResources.GcpsqlInstances:
    case Resources.Database:
      color = '#07BC8A'; //rgb: 7, 188, 138
      break;
    case NativeResources.SimpleStorageService:
      color = '#EEB845';
      break;
    case IconTypes.S3Folder:
      color = '#57B783';
      break;
    case Resources.ServerlessCompute:
    case IconTypes.Serverless:
    case Resources.VirtualMachine:
    case NativeResources.LambdaFunction:
    case NativeResources.Ec2Instance:
    case NativeResources.Ec2AutoScalingGroup:
    case NativeResources.Ec2LaunchTemplate:
    case NativeResources.EcsContainerInstance:
    case CustomIconTypes.CloudPosture:
    case NativeResources.GcpComputeEngine:
    case NativeResources.GcpCloudFunctions:
    case NativeResources.GcpComputeEngineAutoscaler:
    case NativeResources.GcpCloudTpus:
      color = '#1862FF'; //rgb: 24, 98, 255
      break;
    case NativeResources.SystemsManager:
    case NativeResources.SystemsManagerDocument:
    case NativeResources.AmazonMachineImage:
    case NativeResources.ElasticContainerRegistry:
    case NativeResources.GcpImage:
    case Resources.Containers:
    case Resources.Repository:
    case Resources.Image:
      color = '#0DACFF'; //rgb: 13, 172, 255
      break;
    case NativeResources.Route53HealthCheck:
    case NativeResources.Route53HostedZone:
    case NativeResources.GcpCloudDnsZone:
    case NativeResources.GcpCloudDomainsRegistration:
    case 'Route53RecordSet':
      color = '#EDC543'; //rgb: 237,197,676
      break;
    case 'Route53TrafficPolicy':
      color = '#31B616'; //rgb: 49, 182, 22
      break;
    case NativeResources.AccountRoot:
    case IconTypes.Permission:
      color = '#D6221F'; //rgb: 214,34,31
      break;
    case NativeResources.IamGroup:
    case NativeResources.IamUser:
    case NativeResources.IamRole:
    case NativeResources.GcpUser:
    case NativeResources.GcpRole:
    case NativeResources.GcpGroup:
    case Resources.Group:
    case Resources.User:
    case Resources.Role:
      color = '#6D04EB'; //rgb: 109,4,235
      break;
    case NativeResources.CloudTrail:
    case NativeResources.GcpCloudLogMetrics:
      color = '#B72A4D'; //rgb: 183, 42, 77
      break;
    case NativeResources.CloudWatchAlarms:
    case NativeResources.CloudWatchLogs:
    case NativeResources.CloudWatchLogGroup:
    case NativeResources.CloudWatchMetrics:
    case NativeResources.CloudWatchMetricAlarms:
      color = '#04724d'; //rgb: 4, 114, 77
      break;
    case NativeResources.SimpleNotificationService:
    case NativeResources.GcpPubSubLite:
    case Resources.Notification:
      color = '#9083AF'; //rgb: 144, 131, 175
      break;
    case NativeResources.SimpleQueueService:
    case NativeResources.GcpCloudTasksQueue:
    case Resources.AppMessaging:
      color = '#E5B710'; //rgb: 229, 183, 16
      break;
    // case NativeResources.SagemakerEndpoints:
    // case NativeResources.SagemakerNotebookInstances:
    //   color = '#9D63F7'; //rgb: 157, 99, 247
    //   break;
    case NativeResources.CloudFront:
    case NativeResources.SecurityGroup:
    case NativeResources.DhcpOptions:
    case NativeResources.ElasticIp:
    case NativeResources.ElasticLoadBalancer:
    case NativeResources.ElasticLoadBalancerv2:
    case NativeResources.InternetGateway:
    case NativeResources.NatGateway:
    case NativeResources.NetworkAcl:
    case NativeResources.ElasticNetworkInterface:
    case NativeResources.PrefixList:
    case NativeResources.DatabaseSecurityGroup:
    case NativeResources.Subnet:
    case Resources.Subnets:
    case Resources.ContentDeliveryNetwork:
    case NativeResources.SubnetRouteTable:
    case NativeResources.TransitGateway:
    case NativeResources.TransitGatewayAttachment:
    case NativeResources.TransitGatewayRouteTable:
    case NativeResources.VirtualPrivateCloud:
    case NativeResources.VpcFlowLogs:
    case NativeResources.VpcPeeringConnection:
    case NativeResources.AwsVirtualPrivateNetwork:
    case NativeResources.VpnGateway:
    case NativeResources.WafRegional:
    case NativeResources.Wafv1:
    case NativeResources.Wafv2:
    case NativeResources.GcpVirtualPrivateCloud:
    case NativeResources.GcpSubnetwork:
    case NativeResources.GcpRoute:
    case NativeResources.GcpurlMaps:
    case NativeResources.GcpNetworkInterface:
    case NativeResources.GcpCloudNat:
    case NativeResources.GcpvpnGateway:
    case IconTypes.Route:
    case Resources.P2P:
    case Categories.Network:
      color = '#ED7D32'; //rgb: 237, 125, 50
      break;
    case Resources.Account:
    case NativeResources.AwsAccount:
    case NativeResources.AwsOrganization:
    case NativeResources.OrganizationUnit:
    case NativeResources.GcpOrganization:
    case NativeResources.GcpFolder:
    case NativeResources.GcpServiceAccount:
      color = '#012966'; //rgb: 1, 41, 102
      break;
    case NativeResources.ElasticBeanstalk:
      color = '#0A5EFF'; //rgb: 10, 194, 255
      break;
    case NativeResources.AccountPasswordIdentityPolicy:
    case NativeResources.AsgResourcePolicy:
    case NativeResources.IamPolicy:
    case NativeResources.ResourcePolicy:
    case NativeResources.IamInlinePolicy:
    case NativeResources.OrganizationPolicy:
    case NativeResources.GcpOrgPolicyService:
    case NativeResources.GcpiamPolicy:
    case NativeResources.GcpResourcePolicy:
      color = '#31B616'; //rgb: 49, 182, 22
      break;
    case NativeResources.SnowflakeTables:
    case NativeResources.SnowflakeUsers:
    case NativeResources.SnowflakeDatabases:
    case NativeResources.SnowflakeRoles:
    case CloudProviders.Snowflake:
    case CloudProvider.Snowflake:
      color = '#59b3e3';
      break;
    case 'aws':
    case CustomIconTypes.AWS:
    case CloudProviders.Aws:
    case CloudProvider.Aws:
    case ComplianceStandards.CisAws14:
    case ComplianceStandards.CisAws15:
      color = '#FF9900';
      break;
    case 'azure':
    case CloudProviders.Azure:
    case ComplianceStandards.CisAzureV300:
    case ComplianceStandards.MicrosoftAzureFoundationsBenchmarkV600:
      color = '#008CDB';
      break;
    case NativeResources.AzureAdGroup:
    case NativeResources.AzureAdOrganization:
    case NativeResources.AzureAdUser:
    case CloudProviders.AzureAd:
    case 'azureAd':
      color = '#00bef2';
      break;
    case 'gcp':
    case CloudProviders.Gcp:
    case ComplianceStandards.CisGcp300:
    case ComplianceStandards.CisGcp400:
      color = customTheme.colors.green['300'];
      break;
    // Use Google blue for the Google Workspace Foundations benchmark label.
    case 'Google Workspace Foundations Benchmark v1.4.0':
      color = '#4285F4';
      break;
    case 'okta':
    case NativeResources.OktaUser:
    case NativeResources.OktaGroup:
    case NativeResources.OktaOrganization:
    case CloudProviders.Okta:
    case CloudProvider.Okta:
    case CustomIconTypes.Okta:
      color = '#00297a';
      break;
    case NativeResources.GitLabUser:
    case NativeResources.GitLabGroup:
    case NativeResources.GitLabProject:
    case SupportedServices.GitLabBranch:
    case CloudProviders.Gitlab:
    case CloudProviders.GitlabIac:
    case CustomIconTypes.GitLab:
      color = '#d14f36';
      break;
    case NativeResources.GitHubRepository:
    case NativeResources.GitHubUser:
    case NativeResources.GitHubGroup:
    case SupportedServices.GitHubBranch:
    case CloudProviders.Github:
    case CloudProviders.GithubIac:
    case CustomIconTypes.GitHub:
      color = '#25292f';
      break;
    case NativeResources.BitBucketRepository:
    case SupportedServices.BitBucketBranch:
    case CloudProviders.BitbucketIac:
    case CloudProviders.Bitbucket:
    case CustomIconTypes.BitBucket:
      color = '#4089f7';
      break;
    case CustomIconTypes.PII:
    case 'pii':
      color = customTheme.colors.primary;
      break;
    case CustomIconTypes.PHI:
    case 'phi':
      color = customTheme.colors.skyBlue['300'];
      break;
    case CustomIconTypes.PCI:
    case 'pci':
      color = customTheme.colors.blue['300'];
      break;
    case ComplianceStandards.PciDssV321:
    case ComplianceStandards.PciDssV401:
      color = '#02797E';
      break;
    case CustomIconTypes.SENSITIVE:
    case CustomIconTypes.Sensitive:
      color = '#da8b31';
      break;
    case IconTypes.Stopped:
      color = customTheme.colors?.green['300'];
      break;
    case 'critical':
    case 'Critical':
    case 'CRITICAL':
    case IconTypes.Running:
    case ReportTypes.VulnerabilitiesDetailed:
    case ReportTypes.VulnerabilitiesSummary:
    case V1CloudEventsInsightCategory.Malicious:
      color = customTheme.colors?.critical;
      break;
    case 'medium':
    case 'Medium':
    case 'MEDIUM':
      color = customTheme.colors?.medium;
      break;
    case 'low':
    case 'Low':
    case 'LOW':
      color = customTheme.colors?.low;
      break;
    case 'high':
    case 'High':
    case 'HIGH':
    case ReportTypes.Issues:
    case 'RESTRICTED':
      color = customTheme.colors?.high;
      break;
    case 'Unknown':
    case 'unknown':
    case 'UNKNOWN':
      color = customTheme.colors?.gray['250'];
      break;
    case 'green':
      color = 'green';
      break;
    case 'orange':
      color = 'orange';
      break;
    case 'red':
      color = 'red';
      break;
    case 'passed':
      color = 'passed';
      break;
    case 'insights':
      color = customTheme.colors?.insights;
      break;
    case ReportTypes.Compliance:
      color = customTheme.colors?.blue['300'];
      break;
    case V1CloudEventsInsightCategory.Anomalous:
    case 'CONFIDENTIAL':
      color = customTheme.colors?.orange['400'];
      break;
    case LibraryType.Nodejs:
      color = '#83CD29';
      break;
    case ApplicationEnum.ApacheHttp:
      color = '#be202e';
      break;
    case LibraryType.Java:
      color = '#5382A1';
      break;
    case ApplicationEnum.Nginx:
      color = '#019639';
      break;
    case ApplicationEnum.PostgreSql:
      color = '#336791';
      break;
    case ApplicationEnum.MariaDb:
      color = '#C49A6C';
      break;
    case CustomIconTypes.Ubuntu:
      color = '#E95420';
      break;
    case CustomIconTypes.DebianLinux:
    case CustomIconTypes.Debian:
    case CustomIconTypes.debian:
    case 'deb':
      color = '#D70751';
      break;
    case ComplianceStandards.Soc2:
    case IconTypes.SOC2:
    case ComplianceStandards.Iso27001:
    case ComplianceStandards.Iso27701:
    case CustomIconTypes.ISO27001_2022:
    case CustomIconTypes.ISO27701_2019:
    case CustomIconTypes.ISO27701:
    case CustomIconTypes.ISO_27701:
      color = '#0398CC';
      break;
    default:
      color = customTheme.colors.primary;
      if (includes(type, 'ubuntu')) color = '#E95420';
  }
  return color;
};
